import { useState, useEffect, useCallback } from 'react';
import {
  ShieldAlert,
  Building2,
  Users,
  Server,
  Activity,
  Search,
  Plus,
  ArrowUpRight,
  RefreshCw,
  AlertCircle,
  Gauge,
  LogIn,
  SlidersHorizontal,
  Tags,
} from 'lucide-react';
import { api } from '../services/api';
import {
  AdminStats,
  AdminOrgItem,
  AdminUserItem,
  AdminProductItem,
  AuditEventItem,
  OrganisationStatus,
} from '../types';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { Tabs } from '../components/Tabs';
import { Dialog } from '../components/Dialog';
import { Alert } from '../components/Alert';
import { Loader } from '../components/Loader';
import { ParametersPanel } from '../components/ParametersPanel';
import { OrgCommerceDialog, PlansPanel } from '../components/AdminCommerce';
import { useAuth } from '../context/AuthContext';

type AdminTab = 'overview' | 'orgs' | 'users' | 'products' | 'plans' | 'audit' | 'parameters';

function statusTone(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (status) {
    case 'active':
      return 'success';
    case 'suspended':
      return 'warning';
    case 'closed':
      return 'danger';
    default:
      return 'neutral';
  }
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

interface StatCardProps {
  label: string;
  value: number | string;
  hint?: string;
  icon: React.ReactNode;
  glow?: boolean;
}

function StatCard({ label, value, hint, icon, glow }: StatCardProps) {
  return (
    <Card padding="var(--space-4)" glow={glow}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', fontWeight: 'var(--fw-medium)' }}>
          {label}
        </span>
        {icon}
      </div>
      <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--fw-bold)', marginTop: 8, color: 'var(--text-primary)' }}>
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginTop: 4 }}>{hint}</div>
      )}
    </Card>
  );
}

export function AdminPortalPage({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { session, switchOrg, refreshSession } = useAuth();

  const [activeTab, setActiveTab] = useState<AdminTab>('overview');

  const [stats, setStats] = useState<AdminStats | null>(null);
  const [orgs, setOrgs] = useState<AdminOrgItem[]>([]);
  const [users, setUsers] = useState<AdminUserItem[]>([]);
  const [products, setProducts] = useState<AdminProductItem[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditEventItem[]>([]);

  const [orgSearch, setOrgSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Create org dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgLegal, setNewOrgLegal] = useState('');
  const [newOrgEmail, setNewOrgEmail] = useState('');
  const [newOrgCountry, setNewOrgCountry] = useState('FR');

  // Edit org dialog
  const [editOrg, setEditOrg] = useState<AdminOrgItem | null>(null);
  const [commerceOrg, setCommerceOrg] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editLegal, setEditLegal] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editStatus, setEditStatus] = useState<OrganisationStatus>('active');

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [s, o, u, p, a] = await Promise.all([
        api.admin.getStats(),
        api.admin.listOrgs(),
        api.admin.listUsers(),
        api.admin.listProducts(),
        api.admin.listAudit(),
      ]);
      setStats(s);
      setOrgs(o);
      setUsers(u);
      setProducts(p);
      setAuditLogs(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load platform telemetry');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Debounced org search
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        setOrgs(await api.admin.listOrgs(orgSearch || undefined));
      } catch {
        /* keep previous list */
      }
    }, 250);
    return () => clearTimeout(t);
  }, [orgSearch]);

  // Debounced user search
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        setUsers(await api.admin.listUsers(userSearch || undefined));
      } catch {
        /* keep previous list */
      }
    }, 250);
    return () => clearTimeout(t);
  }, [userSearch]);

  const handleCreateOrg = async () => {
    if (!newOrgName.trim()) {
      setError('Organisation display name is required');
      return;
    }
    try {
      setBusy(true);
      setError(null);
      const created = await api.admin.createOrg({
        display_name: newOrgName.trim(),
        legal_name: newOrgLegal.trim() || null,
        billing_email: newOrgEmail.trim() || null,
        country: newOrgCountry.trim().toUpperCase() || 'FR',
      });
      setCreateOpen(false);
      setNewOrgName('');
      setNewOrgLegal('');
      setNewOrgEmail('');
      setNewOrgCountry('FR');
      setNotice(`Tenant "${created.display_name}" created (${created.slug})`);
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tenant');
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (org: AdminOrgItem) => {
    setEditOrg(org);
    setEditName(org.display_name);
    setEditLegal(org.legal_name || '');
    setEditEmail(org.billing_email || '');
    setEditStatus(org.status);
    setError(null);
  };

  const handleSaveOrg = async () => {
    if (!editOrg) return;
    try {
      setBusy(true);
      setError(null);
      await api.admin.updateOrg(editOrg.id, {
        display_name: editName.trim(),
        legal_name: editLegal.trim() || null,
        billing_email: editEmail.trim() || null,
        status: editStatus,
      });
      setEditOrg(null);
      setNotice('Tenant updated');
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tenant');
    } finally {
      setBusy(false);
    }
  };

  const handleImpersonate = async (org: AdminOrgItem) => {
    try {
      setBusy(true);
      setError(null);
      await api.admin.enterOrg(org.id);
      await refreshSession();
      await switchOrg(org.id);
      onNavigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enter tenant workspace');
      setBusy(false);
    }
  };

  const handleToggleAdmin = async (user: AdminUserItem) => {
    if (user.id === session?.principal.id) {
      setError('You cannot change your own platform admin flag');
      return;
    }
    try {
      setBusy(true);
      setError(null);
      await api.admin.setPlatformAdmin(user.id, !user.is_platform_admin);
      setNotice(
        `${user.email || user.display_name} is ${!user.is_platform_admin ? 'now' : 'no longer'} a platform admin`
      );
      setUsers(await api.admin.listUsers(userSearch || undefined));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update platform admin flag');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)', width: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ShieldAlert size={22} color="var(--danger)" />
            <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--fw-bold)', letterSpacing: '-0.02em' }}>
              Platform Admin Console
            </h1>
            <Badge tone="danger" dot>
              GLOBAL SCOPE
            </Badge>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginTop: 4 }}>
            Cross-tenant control plane — organisations, identity directory, product fleet and audit trail.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Button variant="secondary" size="sm" icon={<RefreshCw size={14} />} onClick={loadAll} disabled={loading || busy}>
            Refresh
          </Button>
          <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setCreateOpen(true)} disabled={busy}>
            New Tenant
          </Button>
        </div>
      </div>

      {error && (
        <Alert tone="danger" title="Admin action failed" icon={<AlertCircle size={18} />}>
          {error}
        </Alert>
      )}
      {notice && !error && (
        <Alert tone="success" title="Done" icon={<Gauge size={18} />}>
          {notice}
        </Alert>
      )}

      <Tabs
        activeId={activeTab}
        onChange={(id) => setActiveTab(id as AdminTab)}
        tabs={[
          { id: 'overview', label: 'Fleet Overview', icon: <Gauge size={16} /> },
          { id: 'orgs', label: 'Organisations', icon: <Building2 size={16} />, badge: orgs.length },
          { id: 'users', label: 'Users', icon: <Users size={16} />, badge: users.length },
          { id: 'products', label: 'Product Instances', icon: <Server size={16} />, badge: products.length },
          { id: 'plans', label: 'Plans', icon: <Tags size={16} /> },
          { id: 'audit', label: 'Global Audit', icon: <Activity size={16} />, badge: auditLogs.length },
          { id: 'parameters', label: 'Parameters', icon: <SlidersHorizontal size={16} /> },
        ]}
      />

      {loading && !stats ? (
        <Loader size={36} label="Loading platform telemetry…" />
      ) : (
        <>
          {/* OVERVIEW */}
          {activeTab === 'overview' && stats && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--space-4)' }}>
                <StatCard
                  label="Organisations"
                  value={stats.total_organisations}
                  hint={`${stats.active_organisations} active`}
                  icon={<Building2 size={18} color="var(--accent)" />}
                  glow
                />
                <StatCard
                  label="Principals"
                  value={stats.total_principals}
                  hint={`${stats.total_memberships} memberships`}
                  icon={<Users size={18} color="var(--accent)" />}
                />
                <StatCard
                  label="Hosting Instances"
                  value={stats.hosting_instances}
                  hint="CYRN Hosting fleet"
                  icon={<Server size={18} color="var(--success)" />}
                />
                <StatCard
                  label="Threat Intel Instances"
                  value={stats.intel_instances}
                  hint="CYRN Threat Intel fleet"
                  icon={<ShieldAlert size={18} color="var(--warning)" />}
                />
              </div>

              <Card padding="var(--space-5)">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--fw-semibold)' }}>Recent tenants</h3>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('orgs')}>
                    View all
                  </Button>
                </div>

                {orgs.length === 0 ? (
                  <div style={{ padding: 'var(--space-6)', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
                    No customer organisations yet. Create the first tenant to get started.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {orgs.slice(0, 6).map((org) => (
                      <div
                        key={org.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                          padding: '12px 14px',
                          background: 'var(--bg-inset)',
                          borderRadius: 'var(--radius-md)',
                          border: '1px solid var(--border-subtle)',
                          flexWrap: 'wrap',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                          <div
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 'var(--radius-sm)',
                              background: 'var(--bg-surface-raised)',
                              border: '1px solid var(--border-subtle)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                              color: 'var(--accent)',
                              flexShrink: 0,
                            }}
                          >
                            {org.display_name.charAt(0).toUpperCase()}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 'var(--fw-medium)', fontSize: 'var(--text-sm)' }}>{org.display_name}</div>
                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
                              {org.slug} · {org.members_count} members · {org.products_count} products
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Badge tone={statusTone(org.status)}>{org.status.toUpperCase()}</Badge>
                          <Button size="sm" variant="secondary" icon={<LogIn size={14} />} onClick={() => handleImpersonate(org)} disabled={busy}>
                            Enter
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* ORGANISATIONS */}
          {activeTab === 'orgs' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Search size={16} color="var(--text-tertiary)" style={{ flexShrink: 0 }} />
                <Input
                  placeholder="Search by name, slug, legal name or billing email…"
                  value={orgSearch}
                  onChange={(e) => setOrgSearch(e.target.value)}
                />
              </div>

              {orgs.length === 0 ? (
                <Card padding="var(--space-8)">
                  <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
                    No organisations match this query.
                  </div>
                </Card>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 'var(--space-4)' }}>
                  {orgs.map((org) => (
                    <Card key={org.id} padding="var(--space-5)" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
                          <div
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: 'var(--radius-md)',
                              background: 'var(--bg-inset)',
                              border: '1px solid var(--border-subtle)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                              color: 'var(--accent)',
                              flexShrink: 0,
                            }}
                          >
                            {org.display_name.charAt(0).toUpperCase()}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 'var(--fw-semibold)', fontSize: 'var(--text-base)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {org.display_name}
                            </div>
                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>
                              {org.slug}
                            </div>
                          </div>
                        </div>
                        <Badge tone={statusTone(org.status)}>{org.status.toUpperCase()}</Badge>
                      </div>

                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 8,
                          padding: '10px 12px',
                          background: 'var(--bg-inset)',
                          borderRadius: 'var(--radius-md)',
                          fontSize: 'var(--text-xs)',
                        }}
                      >
                        <div>
                          <span style={{ color: 'var(--text-tertiary)' }}>Members </span>
                          <strong style={{ color: 'var(--text-primary)' }}>{org.members_count}</strong>
                        </div>
                        <div>
                          <span style={{ color: 'var(--text-tertiary)' }}>Products </span>
                          <strong style={{ color: 'var(--text-primary)' }}>{org.products_count}</strong>
                        </div>
                        <div>
                          <span style={{ color: 'var(--text-tertiary)' }}>Country </span>
                          <strong style={{ color: 'var(--text-primary)' }}>{org.country}</strong>
                        </div>
                        <div>
                          <span style={{ color: 'var(--text-tertiary)' }}>Type </span>
                          <strong style={{ color: 'var(--text-primary)' }}>{org.type.toUpperCase()}</strong>
                        </div>
                      </div>

                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                        Created {fmtDate(org.created_at)}
                        {org.billing_email ? ` · ${org.billing_email}` : ''}
                      </div>

                      <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
                        <Button size="sm" variant="primary" style={{ flex: 1 }} icon={<LogIn size={14} />} onClick={() => handleImpersonate(org)} disabled={busy}>
                          Enter workspace
                        </Button>
                        <Button size="sm" variant="secondary" icon={<ArrowUpRight size={14} />} onClick={() => openEdit(org)} disabled={busy}>
                          Manage
                        </Button>
                        <Button size="sm" variant="secondary" icon={<Tags size={14} />} onClick={() => setCommerceOrg(org.id)} disabled={busy}>
                          Plans
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* USERS */}
          {activeTab === 'users' && (
            <Card padding="0">
              <div style={{ padding: 'var(--space-4)', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 10 }}>
                <Search size={16} color="var(--text-tertiary)" style={{ flexShrink: 0 }} />
                <Input
                  placeholder="Filter by email, display name or SSO subject…"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 'var(--text-xs)' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-inset)', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-tertiary)' }}>
                      <th style={{ padding: '12px 16px', fontWeight: 600 }}>Principal</th>
                      <th style={{ padding: '12px 16px', fontWeight: 600 }}>SSO subject</th>
                      <th style={{ padding: '12px 16px', fontWeight: 600 }}>Platform role</th>
                      <th style={{ padding: '12px 16px', fontWeight: 600 }}>Tenants</th>
                      <th style={{ padding: '12px 16px', fontWeight: 600 }}>Last seen</th>
                      <th style={{ padding: '12px 16px', fontWeight: 600, textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontWeight: 'var(--fw-medium)', color: 'var(--text-primary)' }}>
                            {u.display_name || '—'}
                          </div>
                          <div style={{ color: 'var(--text-tertiary)' }}>{u.email || '—'}</div>
                        </td>
                        <td style={{ padding: '12px 16px', fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>{u.subject}</td>
                        <td style={{ padding: '12px 16px' }}>
                          {u.is_platform_admin ? <Badge tone="danger">PLATFORM ADMIN</Badge> : <Badge tone="neutral">USER</Badge>}
                        </td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>{u.organisations_count}</td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-tertiary)' }}>{fmtDate(u.last_seen_at)}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                          <Button
                            size="sm"
                            variant={u.is_platform_admin ? 'danger' : 'secondary'}
                            onClick={() => handleToggleAdmin(u)}
                            disabled={busy || u.id === session?.principal.id}
                          >
                            {u.is_platform_admin ? 'Revoke admin' : 'Grant admin'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--text-tertiary)' }}>
                          No principals match this query.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* PRODUCTS */}
          {activeTab === 'products' && (
            <>
              {products.length === 0 ? (
                <Card padding="var(--space-8)">
                  <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
                    No product instances provisioned across the fleet yet.
                  </div>
                </Card>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 'var(--space-4)' }}>
                  {products.map((prod) => (
                    <Card key={prod.id} padding="var(--space-4)" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {prod.product === 'hosting' ? (
                            <Server size={18} color="var(--success)" />
                          ) : (
                            <ShieldAlert size={18} color="var(--warning)" />
                          )}
                          <span style={{ fontWeight: 'var(--fw-semibold)', fontSize: 'var(--text-sm)' }}>
                            {prod.product === 'hosting' ? 'CYRN Hosting' : 'CYRN Threat Intel'}
                          </span>
                        </div>
                        <Badge tone={statusTone(prod.status)}>{prod.status.toUpperCase()}</Badge>
                      </div>

                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                        Tenant <strong style={{ color: 'var(--text-primary)' }}>{prod.organisation_name}</strong>{' '}
                        <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)' }}>({prod.organisation_slug})</span>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: 'var(--text-xs)',
                          color: 'var(--text-tertiary)',
                          marginTop: 'auto',
                          paddingTop: 8,
                          borderTop: '1px solid var(--border-subtle)',
                          gap: 8,
                          flexWrap: 'wrap',
                        }}
                      >
                        <span style={{ fontFamily: 'var(--font-mono)' }}>{prod.id.slice(0, 8)}…</span>
                        <span>Provisioned {fmtDate(prod.provisioned_at)}</span>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'plans' && <PlansPanel />}

          {/* AUDIT */}
          {activeTab === 'audit' && (
            <Card padding="0">
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 'var(--text-xs)' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-inset)', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-tertiary)' }}>
                      <th style={{ padding: '12px 16px', fontWeight: 600 }}>Timestamp</th>
                      <th style={{ padding: '12px 16px', fontWeight: 600 }}>Action</th>
                      <th style={{ padding: '12px 16px', fontWeight: 600 }}>Actor</th>
                      <th style={{ padding: '12px 16px', fontWeight: 600 }}>Target</th>
                      <th style={{ padding: '12px 16px', fontWeight: 600 }}>Metadata</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log) => (
                      <tr key={log.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '12px 16px', color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{fmtDate(log.created_at)}</td>
                        <td style={{ padding: '12px 16px', fontFamily: 'var(--font-mono)', fontWeight: 'var(--fw-medium)', color: 'var(--accent)' }}>
                          {log.action}
                        </td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>{log.actor_type}</td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>
                          {log.target_type ? `${log.target_type}:${(log.target_id || '').slice(0, 8)}` : '—'}
                        </td>
                        <td style={{ padding: '12px 16px', fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {JSON.stringify(log.event_metadata)}
                        </td>
                      </tr>
                    ))}
                    {auditLogs.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--text-tertiary)' }}>
                          No audit events recorded yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* PARAMETERS */}
          {activeTab === 'parameters' && <ParametersPanel />}
        </>
      )}

      {/* CREATE TENANT DIALOG */}
      <Dialog
        open={createOpen}
        title="Create customer organisation"
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateOrg} disabled={busy || !newOrgName.trim()}>
              {busy ? 'Creating…' : 'Create tenant'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <Input
            label="Display name"
            placeholder="Acme Corp"
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
          />
          <Input
            label="Legal name"
            placeholder="Acme Corp SAS"
            value={newOrgLegal}
            onChange={(e) => setNewOrgLegal(e.target.value)}
          />
          <Input
            label="Billing email"
            type="email"
            placeholder="billing@acme.example"
            value={newOrgEmail}
            onChange={(e) => setNewOrgEmail(e.target.value)}
          />
          <Input
            label="Country code"
            placeholder="FR"
            mono
            value={newOrgCountry}
            onChange={(e) => setNewOrgCountry(e.target.value)}
            hint="ISO 3166-1 alpha-2, used for VAT and invoicing rules."
          />
        </div>
      </Dialog>

      {/* EDIT TENANT DIALOG */}
      <Dialog
        open={Boolean(editOrg)}
        title={editOrg ? `Manage ${editOrg.display_name}` : 'Manage tenant'}
        onClose={() => setEditOrg(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditOrg(null)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveOrg} disabled={busy}>
              {busy ? 'Saving…' : 'Save changes'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <Input label="Display name" value={editName} onChange={(e) => setEditName(e.target.value)} />
          <Input label="Legal name" value={editLegal} onChange={(e) => setEditLegal(e.target.value)} />
          <Input label="Billing email" type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
          <Select
            label="Lifecycle status"
            value={editStatus}
            onChange={(e) => setEditStatus(e.target.value as OrganisationStatus)}
            options={[
              { value: 'active', label: 'Active — full access' },
              { value: 'suspended', label: 'Suspended — access blocked' },
              { value: 'closed', label: 'Closed — terminated' },
            ]}
          />
        </div>
      </Dialog>
      <OrgCommerceDialog orgId={commerceOrg} onClose={() => setCommerceOrg(null)} />
    </div>
  );
}
